/*
 *   File : detail.js
 *   Author : https://evoqins.com
 *   Description : detail component
 *   Integrations : NA
 *   Version : 1.0.0
 */
import React, { useEffect, useState } from "react";
import { Tabs } from "../../Components/Navigation";
import { PrimaryButton } from "../../Components/Buttons";
import { useDispatch } from "react-redux";
import {
  setNavLink,
  setPageTitle,
  setSearchEnabled,
} from "../../Redux/Ui/ui.slice";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ExternalInvestments,
  Holdings,
  Orders,
  Profile,
  RebalanceHistory,
  Sip,
} from "./Helper";
import { BasicFilter, Filter } from "../../Components/Filters";
import { APIService } from "../../Service";
import { EmptyScreen, Loader } from "../../Components/Other";

const TAB_DATA = [
  {
    id: 0,
    label: "profile",
    name: "Profile",
  },
  {
    id: 1,
    label: "orders",
    name: "Orders",
  },
  {
    id: 2,
    label: "holdings",
    name: "Holdings",
  },
  {
    id: 3,
    label: "sip_details",
    name: "SIP Details",
  },
  {
    id: 4,
    label: "external_investments",
    name: "External Investments",
  },
  {
    id: 5,
    label: "rebalance_history",
    name: "Rebalance History",
  },
];

export default function UserDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const [filterData, setFilterData] = useState({});
  const [btnService, setBtnService] = useState(() => {});
  const [filterType, setFilterType] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState({});
  const [empty, setEmpty] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const [user, setUser] = useState({});
  const [userDetail, setUserDetail] = useState(null);
  const [curTab, setCurTab] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      _getUserDetail();
    }
  }, [id]);

  useEffect(() => {
    dispatch(setNavLink(["/users"]));
    dispatch(setSearchEnabled(false));
  }, []);

  // API - Get User Detail
  const _getUserDetail = () => {
    setLoading(true);
    let is_post = true;
    let url = "user/detail";
    let data = {
      customer_id: parseInt(id),
    };

    APIService(is_post, url, data)
      .then((response) => {
        if (response.status_code === 200) {
          const data = response.data;
          setUserDetail(data);

          // Set user summary for header cards
          const summary = data.customer_summary;
          setUser({
            profile_image: summary.profie_image || "",
            admin_name: summary.customer_name || "N/A",
            invested_value: summary.total_amount_invested || "₹0.00",
            total_returns: summary.total_returns || "₹0.00",
            absolute_returns: summary.absolute_returns || "0%",
            xirr_returns: summary.xirr || "0%",
            health_score: summary.global_rebalance_opt_out || "N/A",
            kyc_status: data.kyc_details?.kyc_status || "N/A",
            user_status: summary.customer_status || "N/A",
            status: summary.is_blocked ? "Blocked" : "Active",
            is_blocked: summary.is_blocked,
            joined_on: data?.profile?.joined_on,
          });

          // Set page title with user name
          dispatch(
            setPageTitle(["Users", summary.customer_name || "User Detail"]),
          );
        } else {
          toast.dismiss();
          toast.error(response.message || "Failed to fetch user details", {
            type: "error",
            className: "e-toast",
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching user details:", error);
        toast.dismiss();
        toast.error("Failed to fetch user details", {
          type: "error",
          className: "e-toast",
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTabChange = (tabId) => {
    setAppliedFilters([]);
    setFilterData({});
    setFilterType(null);
    setCurTab(tabId);
  };

  // API - Block/Unblock User
  const _handleBlockUser = () => {
    const is_post = true;
    const url = "user/block";
    const data = {
      customer_id: parseInt(id),
    };
    APIService(is_post, url, data)
      .then((response) => {
        if (response.status_code === 200) {
          toast.success(response.message);
          setUser((prev) => ({
            ...prev,
            is_blocked: response.data.is_blocked,
          }));
          console.log(user);
        } else {
          toast.dismiss();
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Block/Unblock error:", error);
        toast.dismiss();
        toast.error("Something went wrong", {
          className: "e-toast",
        });
      });
  };
  // API - Download User List
  const _downloadUserList = () => {
    setDownloadLoading(true);
    const payload = {
      "customer_id": parseInt(id),
    };

    let is_post = true;
    let url = "user/external-portfolio-download";

    APIService(is_post, url, payload)
      .then((response) => {
        if (response.status_code === 200) {
          const link = document.createElement("a");
          link.href = response.data.url;
          link.download = `users_list_${new Date().getTime()}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          toast.success(response.message || "File downloaded successfully", {
            type: "success",
            className: "e-toast",
          });
        } else {
          toast.dismiss();
          toast.error(response.message || "Failed to download report", {
            type: "error",
            className: "e-toast",
          });
        }
      })
      .catch((error) => {
        console.error("Error downloading user list:", error);
        toast.dismiss();
        toast.error("Failed to download report", {
          type: "error",
          className: "e-toast",
        });
      })
      .finally(() => {
        setDownloadLoading(false);
      });
  };

  if (loading) {
    return (
      <div className="row">
        <div className="col-12">
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
          >
            <Loader />
          </div>
        </div>
      </div>
    );
  }

  return empty ? (
    <EmptyScreen message={"No user with current id"} />
  ) : (
    <div className="row">
      <div className="col-12">
        <div className="d-flex flex-column e-glass-card rounded-16 bg-transparent p-24">
          {/* Header  */}
          <div className="row">
            <div className="col-12 ">
              <div className="d-flex w-100 flex-column e-glass-card rounded-16 rg-24 p-24">
                {/* Header section */}
                <div className="d-flex column-gap-2">
                  <div className="d-flex align-items-center text-eerie-black ">
                    <div className="d-flex e-liner-admin-image p-1x rounded-circle align-items-center justify-content-center">
                      {user?.profile_image ? (
                        <img
                          src={user.profile_image}
                          alt={user?.admin_name || "Admin"}
                          width={58}
                          height={58}
                          className="rounded-circle object-fit-cover border-facebook-blue"
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle border-facebook-blue bg-secondary text-white fw-semibold"
                          style={{
                            width: "58px",
                            height: "58px",
                            fontSize: "18px",
                          }}
                        >
                          {(user?.admin_name || "NA")
                            .trim()
                            .slice(0, 2)
                            .toUpperCase()}
                        </div>
                      )}
                    </div>
                    <h5 className="fs-18 ps-19 lh-20 fw-500  text-capitalize">
                      {user?.admin_name}
                    </h5>
                  </div>

                  <div className="ms-auto ">
                    <div className="d-flex align-items-center justify-content-end">
                      <PrimaryButton
                        icon="block"
                        ClassName={`border-copper-red bg-transparent text-copper-red px-3 py-2 fs-14 fw-400 rounded-pill`}
                        size="16px"
                        name={user?.is_blocked ? "Unblock user" : "Block user"}
                        onPress={_handleBlockUser}
                      />
                    </div>
                  </div>
                </div>
                {/* Card section */}
                <div className="row row-cols-lg-5 row-cols-md-4 row-cols-sm-3 row-cols-2  rg-32">
                  {/* Invested Value */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Invested Value
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
                      {user?.invested_value}
                    </p>
                  </div>

                  {/* Total Returns */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Total Returns
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-medium-sea-green m-0">
                      {user?.total_returns}
                    </p>
                  </div>

                  {/* Absolute Returns */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Absolute Returns
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-medium-sea-green m-0">
                      {user?.absolute_returns}
                    </p>
                  </div>

                  {/* XIRR Returns */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      XIRR Returns
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-medium-sea-green m-0">
                      {user?.xirr_returns}
                    </p>
                  </div>

                  {/* Health Score */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Health Score
                    </h4>
                    <p
                      className={`fw-400 fs-18 text-pure-red lh-24 mb-0 ${
                        user?.health_score !== "Very High Risk"
                          ? "text-medium-sea-green"
                          : "text-cg-red"
                      }`}
                    >
                      {user?.health_score}
                    </p>
                  </div>

                  {/* KYC Status */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      KYC Status
                    </h4>
                    <p className="fw-400 fs-18 lh-24 text-medium-sea-green m-0">
                      {user?.kyc_status}
                    </p>
                  </div>

                  {/* User Status */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Investment Status
                    </h4>
                    <p
                      className={`${
                        user?.user_status === "INVESTED"
                          ? "text-medium-sea-green"
                          : "text-cg-red"
                      } fs-18 fw-400 lh-24 m-0`}
                    >
                      {user?.user_status}
                    </p>
                  </div>

                  {/* User Joined on */}
                  <div className="col d-flex flex-column justify-content-between">
                    <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
                      Joined On
                    </h4>
                    <p className={` text-eerie-black fs-18 fw-400 lh-24 m-0`}>
                      {user?.joined_on}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* tab  */}
          <div className="row pt-24 pb-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center pt-24 px-3 pb-3">
                <Tabs
                  data={TAB_DATA}
                  defaultActive={0}
                  onChange={handleTabChange}
                />
                {filterType &&
                  (filterType === 1 ? (
                    <Filter
                      filters={filterData}
                      onApply={(selectedValues) =>
                        setAppliedFilters(selectedValues)
                      }
                    />
                  ) : filterType === 2 ? (
                    <BasicFilter
                      options={filterData}
                      defaultActive={"6"}
                      onChange={(data) => setAppliedFilters(data)}
                    />
                  ) : (
                    <PrimaryButton
                      name="Download Report"
                      loading={downloadLoading}
                      ClassName="rounded-16  bg-midnight-blue lh-24 py-2 px-3 text-white fs-16 fw-400"
                      onPress={() => {
                        _downloadUserList();
                      }}
                    />
                  ))}
              </div>
            </div>
          </div>
          {/* outlet */}
          <div className="row">
            <div className="col-12">
              {curTab === 0 && <Profile userDetail={userDetail} />}
              {curTab === 1 && (
                <Orders
                  id={id}
                  setFilterData={setFilterData}
                  setFilterType={setFilterType}
                  appliedFilters={appliedFilters}
                />
              )}
              {curTab === 2 && (
                <Holdings
                  id={id}
                  setFilterData={setFilterData}
                  setFilterType={setFilterType}
                  appliedFilters={appliedFilters}
                />
              )}
              {curTab === 3 && (
                <Sip
                  id={id}
                  setFilterData={setFilterData}
                  setFilterType={setFilterType}
                  appliedFilters={appliedFilters}
                />
              )}
              {curTab === 4 && (
                <ExternalInvestments
                  id={id}
                  btnService={setBtnService}
                  setFilterData={setFilterData}
                  setFilterType={setFilterType}
                />
              )}
              {curTab === 5 && (
                <RebalanceHistory
                  setFilterData={setFilterData}
                  setFilterType={setFilterType}
                  appliedFilters={appliedFilters}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
